import React, { useState, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import config from '../../config.json';
import DashboardUpcomingCards from './DashboardUpcomingCards';
import './Dashboard.css';

/**
 * DashboardUpcomingTrip Component
 * Displays upcoming trips within a selected time frame
 * Allows users to filter trips by different hour intervals
 * Supports internationalization
 */
export default function DashboardUpcomingTrip() {
    const { t } = useTranslation(); 
    const navigate = useNavigate();
    const [schedules, setSchedules] = useState([]);
    const [filteredSchedules, setFilteredSchedules] = useState([]);
    const [selectedHours, setSelectedHours] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [filterDebug, setFilterDebug] = useState({
        currentTime: null,
        futureTime: null,
        bufferTime: null, 
        results: []
    });

    /**
     * Handles API errors consistently
     * @param {Error} error - The error object from the API call
     */
    const handleApiError = (error) => {
        console.error("API Error:", error);
        if (error.response?.status === 401) {
            navigate("/");
        }
    };

    /**
     * Fetches all schedules for the organization
     * @returns {Array} Array of schedule objects
     */
    const getAllSchedules = async () => {
        try {
            const resp = await axios.get(`${config.baseURL}/schedule/organization`, {
                withCredentials: true,
            });
            return resp.data;
        } catch (error) {
            handleApiError(error);
            return [];
        }
    };

    /**
     * Filters schedules based on selected time frame with improved logic
     * @param {Array} schedules - Array of schedule objects
     * @param {number} hours - Number of hours to look ahead
     * @returns {Array} Filtered array of schedules
     */
    const filterUpcomingSchedules = (schedules, hours) => {
        const currentTime = new Date();
        const futureTime = new Date(currentTime.getTime() + hours * 60 * 60 * 1000);
        
        // Add 5 minutes buffer to future time to be more inclusive
        const bufferTime = new Date(futureTime.getTime() + 5 * 60 * 1000);
        
        // Store debug info
        const debugResults = [];
        
        const filtered = schedules.filter((schedule) => {
            const scheduleTime = new Date(schedule.startTime);
            
            // Check if the schedule is in the future (or very recent past within 5 min)
            const isInFuture = scheduleTime >= new Date(currentTime.getTime() - 5 * 60 * 1000);
            
            // Check if the schedule is within the selected time frame (plus buffer)
            const isWithinTimeFrame = scheduleTime <= bufferTime;
            
            const isUpcoming = isInFuture && isWithinTimeFrame;
            
            // Add to debug info
            debugResults.push({
                id: schedule.id,
                name: schedule.name || 'Unnamed Trip',
                startTime: scheduleTime.toISOString(),
                isIncluded: isUpcoming,
                reason: !isUpcoming ? 
                        (!isInFuture ? "In the past" : "Beyond time frame") : 
                        "Included"
            });
            
            return isUpcoming;
        });
        
        // Sort filtered schedules by start time (earliest first)
        const sortedFiltered = [...filtered].sort((a, b) => {
            return new Date(a.startTime) - new Date(b.startTime);
        });
        
        // Update debug state
        setFilterDebug({
            currentTime: currentTime.toISOString(),
            futureTime: futureTime.toISOString(),
            bufferTime: bufferTime.toISOString(),
            results: debugResults
        });
        
        return sortedFiltered;
    };

    /**
     * Handles time frame selection change
     * @param {Event} event - Select element change event
     */
    const handleTimeFrameChange = (event) => {
        const hours = parseInt(event.target.value);
        setSelectedHours(hours);
        // We'll refilter in the useEffect that depends on selectedHours
    };

    // Fetch schedules when component mounts or selected hours changes
    useEffect(() => {
        let isMounted = true;

        const fetchSchedules = async () => {
            setIsLoading(true);
            try {
                const data = await getAllSchedules();
                if (isMounted && data) {
                    setSchedules(data);
                    const filtered = filterUpcomingSchedules(data, selectedHours);
                    setFilteredSchedules(filtered);
                }
            } catch (error) {
                console.error("Error fetching schedules:", error);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        fetchSchedules();

        // Set up refresh interval (every 5 minutes)
        const intervalId = setInterval(() => {
            fetchSchedules();
        }, 5 * 60 * 1000);

        return () => {
            isMounted = false;
            clearInterval(intervalId);
        };
    }, [selectedHours]);

    return (
        <Box className="upcoming-trips">
            <div className='upcoming-title' >
                <span className='upcoming-title'>{t('upcomingTrips', 'Upcoming Trips')}</span>
                <select
                    value={selectedHours}
                    onChange={handleTimeFrameChange}
                    className="time-selector"
                >
                    <option value={1}>{t('1hour', '1 hour')}</option>
                    <option value={2}>{t('2hours', '2 hours')}</option>
                    <option value={3}>{t('3hours', '3 hours')}</option>
                    <option value={6}>{t('6hours', '6 hours')}</option>
                    <option value={12}>{t('12hours', '12 hours')}</option>
                    <option value={24}>{t('24hours', '24 hours')}</option>
                </select>
            </div>

            <div className='upcoming-data'>
                {isLoading ? (
                    <div className="loading-container">
                        <CircularProgress size={20} />
                        <span>{t('loading', 'Loading...')}</span>
                    </div>
                ) : (
                    <>
                        <DashboardUpcomingCards schedules={filteredSchedules} />
                    </>
                )}
            </div>
        </Box>
    );
}